import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { EmptyState } from 'components/EmptyState'
import Avatar from 'components/Avatar'
import { Id } from 'types/definitions'
import JobProgressionHistoryViewModel, {
  JobProgressionHistoryActor,
  JobProgressionHistoryItem,
} from './JobProgressionHistoryViewModel'
import styles from './JobProgressionHistoryModal.sass'

interface Props {
  vm: JobProgressionHistoryViewModel
}

export default observer(function JobProgressionHistoryModal({ vm }: Props) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      dataTest="job-progression-history-modal"
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Progression history</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        {vm.items.length === 0 ? (
          <EmptyState pad title="No progression history for this job yet." />
        ) : (
          vm.items.map((item) => (
            <ModalSheetSection spaced bordered key={Id.assert(item.id)}>
              <HistoryStep item={item} />
            </ModalSheetSection>
          ))
        )}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} type="default">
            Close
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface HistoryStepProps {
  item: JobProgressionHistoryItem
}

const HistoryStep = observer(function HistoryStep({ item }: HistoryStepProps) {
  return (
    <div className={styles.step} data-test="job-progression-history-step">
      <div className={styles.stepTitle}>{item.title}</div>
      <div className={styles.entry}>
        {item.formattedDateCreated && (
          <span className={styles.time}>{item.formattedDateCreated}</span>
        )}
        <HistoryStepActor actor={item.createdBy} />
      </div>
    </div>
  )
})

interface HistoryStepActorProps {
  actor: JobProgressionHistoryActor | null
}

const HistoryStepActor = observer(function HistoryStepActor({
  actor,
}: HistoryStepActorProps) {
  if (!actor) {
    return <span className={styles.actor}>—</span>
  }

  return (
    <span className={styles.actorMember}>
      <Avatar subject={actor.member} size={20} />
      <span className={styles.actorName}>{actor.member.displayName}</span>
      {actor.roleLabel && (
        <span className={styles.role}>{actor.roleLabel}</span>
      )}
    </span>
  )
})
